import React, { useState } from 'react'
import ClickAwayListener from 'react-click-away-listener'
import { animated, useSpring } from 'react-spring'
import { preventBubblingEvent } from '@mote/helpers/browser'
import {
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  VisuallyHidden
} from '@mote/ui'
import styled, { css, th } from '@xstyled/styled-components'
import { useMotePlayer } from '../contexts/motePlayer.context'
import { VolumeMuteButton } from './Controls'

const ACTIVATOR_WIDTH = '25px'
const ACTIVATOR_MAX_WIDTH = '80px'
const ACTIVATOR_HEIGHT = '25px'
const SLIDER_MIN_WIDTH = '0px'
const SLIDER_MAX_WIDTH = '35px'
const SLIDER_MIN_OPACITY = 0
const SLIDER_MAX_OPACITY = 1

export const Volume = ({ disabled = false }) => {
  const {
    onVolumeChange: changeVolume,
    toggleMute,
    muted,
    internalVolume: volume
  } = useMotePlayer()
  const [{ width, sliderWidth, opacity }, api] = useSpring(() => ({
    width: ACTIVATOR_WIDTH,
    sliderWidth: SLIDER_MIN_WIDTH,
    opacity: SLIDER_MIN_OPACITY
  }))
  const isSliderFocusedRef = React.useRef(false)
  const isHoveringRef = React.useRef(false)
  const [volumeOpen, setVolumeOpen] = useState(false)

  const toggleAnimation = open => {
    api.start({
      width: open ? ACTIVATOR_MAX_WIDTH : ACTIVATOR_WIDTH,
      sliderWidth: open ? SLIDER_MAX_WIDTH : SLIDER_MIN_WIDTH,
      opacity: open ? SLIDER_MAX_OPACITY : SLIDER_MIN_OPACITY
    })

    setVolumeOpen(open)
  }

  const manualToggle = () => {
    if (volumeOpen) {
      toggleMute()
      return
    }

    toggleAnimation(true)
  }

  const handleVolumeMouseEnter = () => {
    isHoveringRef.current = true
    toggleAnimation(true)
  }

  const handleVolumeMouseLeave = () => {
    isHoveringRef.current = false
    toggleAnimation(false)
  }

  const handleSliderOnFocus = () => {
    if (volumeOpen) return

    isSliderFocusedRef.current = true
    toggleAnimation(true)
  }

  const handleSliderOnBlur = () => {
    if (!isSliderFocusedRef.current) return

    isSliderFocusedRef.current = false

    if (!isHoveringRef.current && volumeOpen) toggleAnimation(false)
  }

  return (
    <VolumeContainer disabled={disabled}>
      <VisuallyHidden>{`Volume: ${volume}%`}</VisuallyHidden>
      <ClickAwayListener onClickAway={() => toggleAnimation(false)}>
        <VolumeActivator
          style={{ width }}
          onMouseEnter={handleVolumeMouseEnter}
          onMouseLeave={handleVolumeMouseLeave}
          tabIndex={-1}
          aria-expanded={volumeOpen}
          aria-controls='mote-volume-slider'
        >
          <StyledVolumeMuteButton
            size={23}
            aria-label={`${muted ? 'Unmute' : 'Mute'} (press m)`}
            isMuted={muted}
            onClick={
              !disabled
                ? e => {
                    e.stopPropagation()
                    toggleMute()
                  }
                : null
            }
            onTouchEnd={
              !disabled
                ? e => {
                    preventBubblingEvent(e)
                    manualToggle()
                  }
                : null
            }
          />
          <SliderContainer
            id='mote-volume-slider'
            aria-hidden={!volumeOpen}
            style={{ width: sliderWidth, opacity }}
          >
            <Slider
              aria-label='Volume'
              size='lg'
              value={volume}
              min={0}
              max={100}
              step={10}
              getAriaValueText={value => `${value}% volume`}
              onChange={changeVolume}
              onFocus={handleSliderOnFocus}
              onBlur={handleSliderOnBlur}
              focusThumbOnChange={false}
              isDisabled={disabled}
            >
              <SliderTrack>
                <SliderFilledTrack />
              </SliderTrack>
              <StyledSliderThumb />
            </Slider>
          </SliderContainer>
        </VolumeActivator>
      </ClickAwayListener>
    </VolumeContainer>
  )
}

const VolumeContainer = styled.div`
  position: relative;
  width: ${ACTIVATOR_WIDTH};
  height: ${ACTIVATOR_HEIGHT};

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.4;
    `};
`

const VolumeActivator = animated(
  styled.div`
    display: flex;
    align-items: center;
    height: ${ACTIVATOR_HEIGHT};
    border: 1px solid ${th.color('grey.l2')};
    border-radius: 12.5px;
    background: white;
    box-sizing: border-box;
  `
)

const SliderContainer = animated(
  styled.div`
    margin-left: 8px;
    height: 100%;
    display: flex;
    align-items: center;
    z-index: 1;
  `
)

const StyledSliderThumb = styled(SliderThumb)`
  width: 12px;
  height: 12px;
  background: ${th.color('primary.base')};
  box-shadow: none;

  &:disabled,
  &[data-disabled] {
    background: ${th.color('disabled.l1')};
  }

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      background: ${th.color('disabled.l1')};
    `};
`

const StyledVolumeMuteButton = styled(VolumeMuteButton)`
  svg {
    height: 16px;
    width: 16px;
  }
`
